import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import { getListingsApiUrl, isProductionEnvironment } from '../utils/apiUrls';

const LISTINGS_URL = getListingsApiUrl();


function DeveloperListings() {
  const { user, isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const [pageSize, setPageSize] = useState(20);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [listingToDelete, setListingToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isProd = isProductionEnvironment();

  // Filters
  const [propertyAddress, setPropertyAddress] = useState('');
  const [listingId, setListingId] = useState('');
  const [uploadDateStart, setUploadDateStart] = useState('');
  const [uploadDateEnd, setUploadDateEnd] = useState('');
  const [expirationDateStart, setExpirationDateStart] = useState('');
  const [expirationDateEnd, setExpirationDateEnd] = useState('');

  const isKeyFloUser = useMemo(() => {
    const email = user?.email || '';
    const ALLOWED_USERS = ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'];
    return ALLOWED_USERS.includes(email);
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isKeyFloUser) navigate('/');
  }, [isAuthenticated, isKeyFloUser, navigate]);

  const formatDateTime = (ts) => {
    if (!ts) return '';
    if (typeof ts === 'string') return ts;
    try { return ts.toISOString(); } catch { return String(ts); }
  };

  const formatDate = (dateStr) => {
    try {
      if (!dateStr) return '';
      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
      const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${m[1]}-${m[2]}-${m[3]}`;
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr || '';
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    } catch { return dateStr || ''; }
  };

  const buildParams = (resetOffset = true) => {
    const params = { limit: pageSize, offset: resetOffset ? 0 : offset, caller_email: user?.email };
    if (propertyAddress) params.property_address = propertyAddress;
    if (listingId) params.listing_id = listingId;
    if (uploadDateStart) params.upload_date_start = new Date(uploadDateStart).toISOString();
    if (uploadDateEnd) {
      const end = new Date(uploadDateEnd);
      end.setHours(23, 59, 59, 999);
      params.upload_date_end = end.toISOString();
    }
    if (expirationDateStart) params.expiration_date_start = expirationDateStart;
    if (expirationDateEnd) params.expiration_date_end = expirationDateEnd;
    return params;
  };

  const fetchListings = async (reset = true) => {
    if (!isAuthenticated || !isKeyFloUser) return;
    try {
      setLoading(true);
      setError('');
      const params = buildParams(reset);
      const response = await axios.get(`${LISTINGS_URL}/dev`, { params });
      if (response.data?.status !== 'success') {
        throw new Error(response.data?.error || 'Failed to fetch listings');
      }
      const newListings = response.data.listings || [];
      setListings(reset ? newListings : [...listings, ...newListings]);
      setTotalCount(response.data.total_count || 0);
      setOffset((reset ? 0 : offset) + newListings.length);
    } catch (e) {
      setError(e.message || 'Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && isKeyFloUser) fetchListings(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isKeyFloUser, pageSize]);

  const getDisplayColumns = () => {
    if (listings.length === 0) return [];
    // Preserve model-defined order from the API response
    const allCols = Object.keys(listings[0]);
    const pinned = ['id', 'street_address'].filter(c => allCols.includes(c));
    const rest = allCols.filter(c => !pinned.includes(c));
    return [...pinned, ...rest];
  };

  const handleDeleteClick = (listing) => {
    setListingToDelete(listing);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!listingToDelete) return;
    try {
      setDeleting(true);
      const response = await axios.delete(`${LISTINGS_URL}/${listingToDelete.id}/hard`);
      if (response.data?.error) throw new Error(response.data.error);
      setListings(prev => prev.filter(l => l.id !== listingToDelete.id));
      setTotalCount(prev => prev - 1);
    } catch (e) {
      alert(e.response?.data?.error || e.message || 'Failed to delete listing');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setListingToDelete(null);
    }
  };

  if (!isAuthenticated) {
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column' }}>
      <p>Please log in</p>
      <button onClick={() => loginWithRedirect({ authorizationParams: { prompt: 'login' } })}>Log in</button>
    </div>
  }

  if (!isKeyFloUser) {
    return (
      <div style={{ padding: '24px' }}>
        <h2>Access Denied</h2>
        <p>This page is restricted to KeyFlo team members.</p>
      </div>
    );
  }

  const displayColumns = getDisplayColumns();

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2>Developer • Listings</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
          <button className="back-button" onClick={() => navigate('/')}>← Back to Contracts</button>
          <label style={{ marginRight: '8px' }}>Page size:</label>
          <select value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value, 10))}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
          <DeveloperNavigation />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '12px', marginBottom: '12px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Property Address</label>
          <input value={propertyAddress} onChange={(e) => setPropertyAddress(e.target.value)} placeholder="street_address" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Listing ID</label>
          <input value={listingId} onChange={(e) => setListingId(e.target.value)} placeholder="listing_id" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Upload Date Start</label>
          <input type="date" value={uploadDateStart} onChange={(e) => setUploadDateStart(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Upload Date End</label>
          <input type="date" value={uploadDateEnd} onChange={(e) => setUploadDateEnd(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Expiration Date Start</label>
          <input type="date" value={expirationDateStart} onChange={(e) => setExpirationDateStart(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Expiration Date End</label>
          <input type="date" value={expirationDateEnd} onChange={(e) => setExpirationDateEnd(e.target.value)} style={{ width: '100%' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button onClick={() => { setOffset(0); fetchListings(true); }}>Apply Filters</button>
        <button onClick={() => {
          setPropertyAddress('');
          setListingId('');
          setUploadDateStart('');
          setUploadDateEnd('');
          setExpirationDateStart('');
          setExpirationDateEnd('');
          setOffset(0);
          fetchListings(true);
        }}>Clear</button>
        <button onClick={() => fetchListings(false)}>Load More</button>
      </div>

      {loading && <LoadingSpinner />}
      {error && <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>}

      <div style={{ marginBottom: '12px' }}>
        Showing {listings.length} of {totalCount} listings
      </div>

      <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb' }}>
        <table style={{ width: 'max-content', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', position: 'sticky', top: 0, left: 0, zIndex: 2, minWidth: 90, width: 90 }}>
                actions
              </th>
              {displayColumns.map((col, colIndex) => {
                const isFrozen = colIndex === 0 || colIndex === 1;
                const leftValue = colIndex === 0 ? 90 : 190;
                const columnWidth = isFrozen ? (colIndex === 0 ? 100 : 250) : undefined;
                return (
                  <th
                    key={col}
                    style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      borderBottom: '1px solid #e5e7eb',
                      background: '#f8fafc',
                      position: isFrozen ? 'sticky' : 'relative',
                      top: 0,
                      left: isFrozen ? `${leftValue}px` : 'auto',
                      zIndex: isFrozen ? 2 : 1,
                      boxShadow: isFrozen && colIndex === 1 ? '2px 0 5px rgba(0,0,0,0.1)' : 'none',
                      width: isFrozen ? `${columnWidth}px` : undefined,
                      minWidth: isFrozen ? `${columnWidth}px` : 150,
                      maxWidth: isFrozen ? `${columnWidth}px` : undefined,
                    }}
                  >
                    {col}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {listings.map((listing, idx) => (
              <tr key={listing.id || idx} style={{ borderBottom: '1px solid #f1f5f9', height: '100px', maxHeight: '100px' }}>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top', position: 'sticky', left: 0, zIndex: 1, background: '#ffffff', minWidth: 90, width: 90 }}>
                  <button
                    onClick={() => handleDeleteClick(listing)}
                    style={{
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                    }}
                  >
                    Delete
                  </button>
                </td>
                {displayColumns.map((col, colIndex) => {
                  let val = listing[col];
                  if (col.includes('_date') || col === 'created_at' || col === 'updated_at') {
                    if (col === 'created_at' || col === 'updated_at') {
                      val = val ? formatDateTime(val) : '';
                    } else {
                      val = val ? formatDate(val) : '';
                    }
                  }
                  if (typeof val === 'boolean') val = val ? 'true' : 'false';
                  if (typeof val === 'number') val = val.toString();
                  if (val === null || typeof val === 'undefined') val = '';

                  const isFrozen = colIndex === 0 || colIndex === 1;
                  const leftValue = colIndex === 0 ? 90 : 190;
                  const columnWidth = isFrozen ? (colIndex === 0 ? 100 : 250) : undefined;

                  return (
                    <td
                      key={col}
                      style={{
                        padding: '10px 12px',
                        borderBottom: '1px solid #f1f5f9',
                        borderRight: '1px solid #f1f5f9',
                        verticalAlign: 'top',
                        whiteSpace: isFrozen ? 'nowrap' : 'pre-wrap',
                        wordBreak: isFrozen ? 'normal' : 'break-word',
                        overflow: isFrozen ? 'hidden' : 'visible',
                        textOverflow: isFrozen ? 'ellipsis' : 'clip',
                        minWidth: isFrozen ? `${columnWidth}px` : 150,
                        width: isFrozen ? `${columnWidth}px` : undefined,
                        maxWidth: isFrozen ? `${columnWidth}px` : undefined,
                        position: isFrozen ? 'sticky' : 'relative',
                        left: isFrozen ? `${leftValue}px` : 'auto',
                        zIndex: isFrozen ? 1 : 0,
                        background: isFrozen ? '#ffffff' : 'transparent',
                        boxShadow: isFrozen && colIndex === 1 ? '2px 0 5px rgba(0,0,0,0.1)' : 'none',
                        boxSizing: 'border-box',
                      }}
                      title={isFrozen ? String(val) : undefined}
                    >
                      <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                        {String(val)}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
            {listings.length === 0 && !loading && (
              <tr>
                <td colSpan={displayColumns.length + 1} style={{ padding: '12px', textAlign: 'center' }}>No listings found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showDeleteModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '8px', width: '420px', maxWidth: '90%' }}>
            <h3 style={{ marginTop: 0, color: '#ef4444' }}>Confirm Delete</h3>
            {isProd && (
              <p style={{ color: '#dc2626', fontWeight: 'bold', fontSize: '1rem', textAlign: 'center', border: '2px solid #dc2626', borderRadius: '4px', padding: '8px' }}>
                THIS IS THE PROD ENV. PLEASE BE 100% SURE YOU WANT TO DELETE.
              </p>
            )}
            <p>
              Are you sure you want to permanently delete listing <strong>#{listingToDelete?.id}</strong>
              {listingToDelete?.street_address ? ` — ${listingToDelete.street_address}` : ''}?
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
              <button
                onClick={() => { setShowDeleteModal(false); setListingToDelete(null); }}
                style={{ background: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                style={{ background: '#ef4444', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
              >
                {deleting ? 'Deleting...' : 'Delete Listing'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeveloperListings;
